/*
  This hash defines all trackable objects in the app.  Anything
  can be trackable (containers, radio buttons, etc.), though the
  most tracked element would be a button element.

   As a convention, the top level of the hash represents the
   screens in the app (CREATE_ACCOUNT, LINK_BANK_SEARCH, etc.).

   Within each screen, we have various objects.  A convention to
   follow would be to add a _BUTTON suffix for buttons, and a
   _CONTAINER suffix for containers, etc.

   This way, all trackable objects within a page can be easily
   located in this hash.
*/

const TRACKER_IDS = {
  BANK_LINK_SUCCESS: {
    NEXT_BUTTON: 'registrationLinkBankAccountLinkedNext',
  },
  CONTACT_INFO: {
    NEXT_BUTTON: 'registrationInformationContactNext',
    BACK_BUTTON: 'registrationInformationContactBack',
  },
  CHOOSE_FUNDING_SOURCE: {
    CONTINUE_BUTTON: 'registrationLinkBankSelectChecking',
  },
  DIRECT_TO_MOBILE: {
    PAGE: 'registrationDirectToMobilePage',
    APP_STORE_BUTTON: 'registrationDirectToMobileAppStore',
    GOOGLE_PLAY_BUTTON: 'registrationDirectToMobileGooglePlay',
    SKIP_DIRECT_TO_MOBILE_BUTTON: 'registrationDirectToMobileSkip',
  },
  ESG_TOUR: {
    CENTER_BUTTON: 'registrationEsgTourCenterButton',
    LEFT_ARROW: 'registrationEsgTourLeftArrow',
    RIGHT_ARROW: 'registrationEsgTourRightArrow',
    X_BUTTON: 'registrationEsgTourXButton',
  },
  FUNDING: {
    LINK_YOUR_BANK_BUTTON: 'registrationLinkBankInterstitialNext',
    AUTOMATIC_BANK_LINK_BUTTON: 'registrationLinkBankInterstitialLinkAutomatically',
    MANUAL_BANK_LINK_BUTTON: 'registrationLinkBankInterstitialLinkManually',
  },
  INSTITUTIONS: {
    MANUAL_LINK_BUTTON: 'registrationLinkBankAccountManualCTA',
    INSTITUTION_CONTAINER: 'registrationLinkBankSearchBank',
    SKIP_LINK_BANK_BUTTON: 'registrationLinkBankSearchDoLater',
    SKIP_LINK_BANK_MODAL: 'registrationLinkBankSearchDoLaterModal',
    SKIP_LINK_BANK_MODAL_CONFIRM: 'registrationLinkBankSearchDoLaterConfirm',
    SKIP_LINK_BANK_MODAL_CANCEL: 'registrationLinkBankSearchDoLaterCancel',
  },
  INVEST_INTERSTITIAL: {
    NEXT_BUTTON: 'registrationInvestInterstitialNext',
  },
  INVESTOR_QUESTIONS: {
    SUBMIT_BUTTON: 'registrationInformationInvestorQuestionsNext',
  },
  LATER_RECOMMENDATION: {
    OPEN_BUTTON: 'laterRegistrationIRARecommendationNext',
  },
  LATER_SUITABILITY_QUESTIONS: {
    LATER_ONBOARDING_NEXT_BUTTON: 'laterRegistrationInvestorQuestions',
    NEXT_BUTTON_1: 'registrationPortfolioQuestionsNext1',
    NEXT_BUTTON_2: 'registrationPortfolioQuestionsNext2',
  },
  MFA: {
    BACK_BUTTON: 'registrationTwoFactorEnterCodeBack',
  },
  MANUAL_FUNDING_SOURCE: {
    ADD_FUNDING_SOURCE_BUTTON: 'registrationLinkBankManualNext',
    BACK_BUTTON: 'registrationLinkBankManualBack',
    SKIP_LINK_BANK_BUTTON: 'registrationLinkBankManualDoLater',
    SKIP_LINK_BANK_MODAL: 'registrationLinkBankManualDoLaterModal',
    SKIP_LINK_BANK_MODAL_CONFIRM: 'registrationLinkBankManualDoLaterConfirm',
    SKIP_LINK_BANK_MODAL_CANCEL: 'registrationLinkBankManualDoLaterCancel',
  },
  PERSONAL_INFO: {
    NEXT_BUTTON: 'registrationInformationPersonalNext',
    BACK_BUTTON: 'registrationInformationPersonalBack',
  },
  PORTFOLIO_OVERVIEW: {
    SUBMIT_BUTTON: 'registrationPortfolioBreakdownNext',
  },
  PORTFOLIO_SELECT_THEME: {
    NEXT: 'registrationPortfolioSelectThemeNext',
    LEARN_MORE: 'registrationPortfolioSelectThemeLearnMore',
  },
  PREFUND: {
    NEXT_BUTTON: 'registrationSpendSpendPrefundNext',
    PREFUND_AMOUNT_BUTTON: 'registrationSpendSpendPrefundAmount',
  },
  PRICING_INTERSTITIAL: {
    NEXT_BUTTON: 'pricingInterstitialNext',
  },
  REFERRAL_AGREEMENT: {
    AGREE_BUTTON: 'referralDisclosureAgree',
    SCROLL_TO_BOTTOM_BUTTON: 'referralDisclosureSignBelow',
    BACK_BUTTON: 'referralDisclosureAgreementBack',
  },
  ROUNDUPS_INTERSTITIAL: {
    NEXT_BUTTON: 'registrationRoundupsInterstitialNext',
    LEARN_MORE: 'registrationRoundupsInterstitialLearnMore',
    SKIP_BUTTON: 'registrationRoundupsInterstitialSkip',
  },
  ROUNDUPS_SETUP: {
    NEXT_BUTTON: 'registrationRoundupsInterstitialNext',
  },
  ROUNDUPS_FUNDING: {
    NEXT_BUTTON: 'registrationRoundupsFundingNext',
  },
  ROUNDUPS_PAST: {
    NEXT_BUTTON: 'registrationRoundupsPastNext',
    SKIP_BUTTON: 'registrationRoundupsPastSkip',
  },
  SECOND_CHANCE_BANK_LINK: {
    LINK_FUNDING_SOURCE_BUTTON: 'registrationSecondChanceLinkBankInterstitialNext',
    DO_THIS_LATER_BUTTON: 'registrationSecondChanceLinkBankInterstitialDoLater',
  },
  SELECT_TIER: {
    SELECT_TIER_BRONZE: 'registrationSelectTierBronzeTierSelected',
    SELECT_TIER_SILVER: 'registrationSelectTierSilverTierSelected',
    SELECT_TIER_GOLD: 'registrationSelectTierGoldTierSelected',
  },
  SIGNUP: {
    GET_STARTED_BUTTON: 'registrationGetStartedCTA',
    CREATE_ACCOUNT_BUTTON: 'registrationCreateAccountLogIn',
    COMPARE_SUBSCRIPTION_TIERS_BUTTON: 'registrationCompareSubscriptionTiers',
    EMAIL_EXISTS_REDIRECT_BUTTON: 'registrationEmailExistsLoginRedirect',
    US_RESIDENT_AGREEMENT_BUTTON: 'registrationCreateAccountResidentAndProgramAgreement',
  },
  SPEND_CORE_INTERSTITIAL: {
    NEXT_BUTTON: 'registrationSpendSpendCoreInterstitialNext',
  },
  SPEND_INTRO: {
    GET_STARTED_TOP: 'registrationSpendSpendIntroCTATop',
    GET_STARTED_BOTTOM: 'registrationSpendSpendIntroCTABottom',
    ACCORDION_ONE: 'registrationSpendSpendIntroAccordionOne',
    ACCORDION_TWO: 'registrationSpendSpendIntroAccordionTwo',
    WELCOME_VIDEO: 'registrationSpendSpendIntroVideo',
  },
  SPEND_ORDER_CONFIRMATION: {
    NEXT_BUTTON: 'registrationSpendSpendOrderConfirmationNext',
  },
  SSN: {
    NEXT_BUTTON: 'registrationSsnNext',
  },
  START_INVESTING: {
    CHANGE_RECURRING_AMOUNT: 'registrationInitialInvestmentRecurringAmount',
    CHANGE_ONE_TIME_AMOUNT: 'registrationInitialInvestmentOneTimeAmount',
    RECURRING_START_INVESTING: 'registrationInitialInvestmentRecurringNext',
    ONE_TIME_START_INVESTING: 'registrationInitialInvestmentOneTimeNext',
    RECURRING_SKIP: 'registrationInitialInvestmentRecurringSkip',
    ONE_TIME_INSTEAD: 'registrationInitialInvestmentRecurringOneTime',
    ONE_TIME_SKIP: 'registrationInitialInvestmentOneTimeSkip',
    LATER_ONBOARDING_ONE_TIME_SKIP: 'laterRegistrationOneTimeInvestmentSkip',
    LATER_ONBOARDING_ONE_TIME_START_INVESTING: 'laterRegistrationOneTimeInvestmentNext',
    LATER_ONBOARDING_RECURRING_SKIP: 'laterRegistrationRecurringInvestmentSkip',
    LATER_ONBOARDING_RECURRING_START_INVESTING: 'laterRegistrationRecurringInvestmentNext',
    REGISTRATION_RECURRING_INVESTMENT_SET_DEFAULT: 'registrationRecurringInvestmentSetDefault',
    REGISTRATION_RECURRING_INVESTMENT_SKIP: 'registrationRecurringInvestmentSkip',
    REGISTRATION_RECURRING_INVESTMENT_ONE_TIME: 'registrationRecurringInvestmentOneTime',
  },
  SUCCESS: {
    APP_STORE_BUTTON: 'registrationSuccessAppStore',
    GOOGLE_PLAY_BUTTON: 'registrationSuccessGooglePlay',
    LOGIN_BUTTON: 'registrationSuccessLogin',
    OPEN_APP_BUTTON: 'registrationSuccessLogin',
    EARLY_TIER: {
      NEXT_BUTTON: 'registrationSuccessEarlyTierNext',
      UNVERIFIED_BUTTON: 'registrationSuccessEarlyTierUnverified',
    },
  },
  SUITABILITY_INTERSTITIAL: {
    NEXT_BUTTON: 'registrationSuitabilityInterstitialNext',
  },
  SUITABILITY_QUESTIONS: {
    NEXT_BUTTON: 'registrationPortfolioQuestionsNext',
  },
  WELCOME: {
    NEXT_BUTTON: 'welcomeGetStarted',
    LATER_ONBOARDING_NEXT_BUTTON: 'laterRegistrationInterstitialCTA',
    LATER_ONBOARDING_MODAL_CANCEL_BUTTON: 'laterRegistrationUpgradeCancel',
    LATER_ONBOARDING_MODAL_CONFIRM_BUTTON: 'laterRegistrationUpgradeConfirm',
    LATER_ONBOARDING_UNQUALIFIED_CANCEL_BUTTON: 'laterRegistrationUnqualifiedModalCancel',
    LATER_ONBOARDING_UNQUALIFIED_CONFIRM_BUTTON: 'laterRegistrationUnqualifiedModalConfirm',
  },
  EVENT: {
    SIGNUP: 'signUpEvent',
  },
  MOC_PROGRESS_STEP_ONE: {
    BACK_BUTTON: 'registrationMOCProgressStepOneBack',
  },
  SHIPPING_ADDRESS: {
    BACK_BUTTON: 'registrationSpendAddressBack',
  },
  SHIPPING_ADDRESS_NOT_FOUND: {
    BACK_BUTTON: 'bankCardOrderAddressNotFoundBack',
  },
  SHIPPING_ADDRESS_EDIT: {
    BACK_BUTTON: 'bankCardOrderEditAddressInputBack',
  },
  SHIPPING_ADDRESS_SUGGESTED: {
    BACK_BUTTON: 'bankCardOrderSuggestedAddressBack',
  },
  CARD_SIGNATURE: {
    BACK_BUTTON: 'registrationSpendSignatureBack',
  },
}

export { TRACKER_IDS }
